import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import useAppNavigate from "hooks/useAppNavigate";
import {
  EnabledCell,
  FrequencyCell,
  IntegrationNameCell,
  TimestampCell,
  timeColumnLabel,
} from "./integrationListCells";
import RunStatusChip from "./RunStatusChip";
import TableSkeletonRows from "./TableSkeletonRows";
import TypeChipList from "./TypeChipList";
import { NO_WRAP_CELL_SX } from "./tableStyles";
import type {
  IntegrationMonitorItem,
  IntegrationMonitorSortField,
} from "features/integrationsBeta/integrationsBeta";
import { usePlatformEnumLabels } from "features/integrationsBeta/platformEnums";
import type { TimezoneDisplayMode } from "features/integrationsBeta/timezoneDisplay";

interface Column {
  key: string;
  label: string;
  /** Fixed proportional width (table-layout: fixed); long content truncates. */
  width: string;
  sortable?: boolean;
  sortField?: IntegrationMonitorSortField;
}

const COLUMNS: Column[] = [
  {
    key: "client_name",
    label: "Client",
    width: "11%",
    sortable: true,
    sortField: "client",
  },
  {
    key: "name",
    label: "Integration",
    width: "27%",
    sortable: true,
    sortField: "name",
  },
  { key: "source_services", label: "Source", width: "5%" },
  { key: "target_services", label: "Target", width: "5%" },
  {
    key: "enabled",
    label: "Enabled",
    width: "5%",
    sortable: true,
    sortField: "enabled",
  },
  { key: "frequency", label: "Frequency", width: "15%" },
  {
    key: "last_run_started_at",
    label: "Last Run",
    width: "12%",
    sortable: true,
    sortField: "last_run_started_at",
  },
  {
    key: "next_run_at",
    label: "Next Run",
    width: "12%",
    sortable: true,
    sortField: "next_run_at",
  },
  {
    key: "last_run_status",
    label: "Last Status",
    width: "8%",
    sortable: true,
    sortField: "last_run_status",
  },
];

type Props = {
  integrations: IntegrationMonitorItem[];
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  sortBy?: IntegrationMonitorSortField;
  sortOrder: "asc" | "desc";
  onSortChange: (field: IntegrationMonitorSortField) => void;
  timezoneMode: TimezoneDisplayMode;
  pagination?: React.ReactNode;
};

/** The internal monitor table: every client, the engine's last status. */
export default function IntegrationsBetaTable({
  integrations,
  isLoading,
  isFetching,
  error,
  sortBy,
  sortOrder,
  onSortChange,
  timezoneMode,
  pagination,
}: Props) {
  const navigate = useAppNavigate();
  const { statusLabel } = usePlatformEnumLabels();

  if (error) {
    return (
      <Typography p={2} variant="h6" align="center" color="error">
        Error loading integrations
      </Typography>
    );
  }

  const onRowClick = (id: number) => navigate(`/integrations-beta/${id}`);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "clip",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto" }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1440,
            width: "100%",
            tableLayout: "fixed",
            borderColor: "divider",
            // Default MUI cell padding (16px) - same left inset as the
            // User Management and Integration Details History tables.
          }}
        >
          <colgroup>
            {COLUMNS.map((col) => (
              <col key={col.key} style={{ width: col.width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {col.sortable && col.sortField ? (
                    <TableSortLabel
                      active={sortBy === col.sortField}
                      direction={sortBy === col.sortField ? sortOrder : "asc"}
                      onClick={() => onSortChange(col.sortField!)}
                    >
                      {timeColumnLabel(col.key, col.label, timezoneMode)}
                    </TableSortLabel>
                  ) : (
                    timeColumnLabel(col.key, col.label, timezoneMode)
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows columns={COLUMNS.length} rows={8} />
            ) : integrations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Typography color="text.secondary">
                    No integrations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              integrations.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => onRowClick(row.id)}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      borderBottom: "none",
                    },
                    opacity: isFetching ? 0.6 : 1,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "calibrate.primarySoft06",
                    },
                  }}
                >
                  <TableCell sx={NO_WRAP_CELL_SX} title={row.client_name}>
                    {row.client_name}
                  </TableCell>
                  <TableCell sx={NO_WRAP_CELL_SX} title={row.name}>
                    <IntegrationNameCell
                      name={row.name}
                      timezone={row.timezone}
                    />
                  </TableCell>
                  <TableCell>
                    <TypeChipList items={row.source_services} />
                  </TableCell>
                  <TableCell>
                    <TypeChipList items={row.target_services} />
                  </TableCell>
                  <TableCell>
                    <EnabledCell enabled={row.enabled} />
                  </TableCell>
                  <TableCell>
                    <FrequencyCell
                      frequency={row.frequency}
                      crons={row.active_schedule_crons}
                      timezone={row.timezone}
                      timezoneMode={timezoneMode}
                    />
                  </TableCell>
                  <TableCell sx={NO_WRAP_CELL_SX}>
                    <TimestampCell
                      iso={row.last_run_started_at}
                      timezone={row.timezone}
                      timezoneMode={timezoneMode}
                    />
                  </TableCell>
                  <TableCell sx={NO_WRAP_CELL_SX}>
                    <TimestampCell
                      iso={row.next_run_at}
                      timezone={row.timezone}
                      timezoneMode={timezoneMode}
                      color="text.secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <RunStatusChip
                      status={row.last_run_status}
                      label={statusLabel(row.last_run_status)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      {pagination}
    </Paper>
  );
}
